// =============================================================================
// ui/tabs/ResultsConfluence.tsx — the Results board's Confluence card. #58.
// -----------------------------------------------------------------------------
// Two readouts stacked in one card, ported from beta's `renderConfs()`
// (app/backtest-dashboard.html:753):
//
//   top    — one bar per confluence, "win rate when present", best first
//   bottom — "Win rate by confluences stacked": 0 / 1 / 2 / 3+ buckets
//
// Copy is verbatim from beta's markup (the card header at :297-303) and from the
// `${b.k} conf` / `n=${g.n}` templates in that render function. Beta's file also
// carries strings for things this card no longer draws, so everything reproduced
// here was traced back to a line that actually executes.
//
// ── 🚨 THE TWO HALVES COUNT DIFFERENTLY AND MUST NOT BE ADDED UP ──
//
// In the TOP half a sample is counted once for EVERY confluence it carried, so a
// trade with three confluences appears in three rows. Those n= figures overlap by
// construction and sum to more than the book. In the LADDER each sample lands in
// exactly one bucket, so those n= figures DO sum to the book.
//
// A reader who adds up the top half, gets a bigger number than they have samples,
// and concludes the board is double-counting their results will stop trusting
// every number on the page. That is why the card states the overlap ON SCREEN —
// `bd-cf-caveat` under the bars and `bd-cf-ladder-note` beside the ladder heading
// — and not only in this comment. Beta says neither, and beta is wrong to.
//
// ── ⚠ THE ZERO-CONFLUENCE ROW IS THE BASELINE, NOT A FOURTH DATA POINT ──
//
// "1 conf", "2 conf" and "3+ conf" only mean something read against "how did it
// go with none". So the ladder is drawn from a FIXED four buckets and the zero
// row is rendered even when the selector hands back nothing for it — an empty
// zero bucket is itself a finding (this trader never takes the setup bare), and
// dropping the row hides that finding behind a ladder that silently starts at 1.
//
// `stackingLadder()` uses `groupBy`, which only emits a bucket that has samples,
// so ANY of the four can be absent from the prop. The backfill below is not
// defensive padding — it is the card refusing to let a missing bucket look like
// a bucket that was never asked about.
//
// ── ⚠ ONE DELIBERATE DIVERGENCE FROM BETA: THE COLOUR OF A DASH ──
//
// A group whose trades all scratched has no win rate. Beta paints that "—" RED in
// the top half (`g.wr>=50?'pos':'neg'` — null fails the test and falls to 'neg')
// while painting the same "—" neutral in the ladder (`g.wr!=null?'neg':'muted'`).
// That is the same fact rendered two ways on one card, and the red one claims a
// bad result where there is no result. Both halves are neutral here.
// =============================================================================

import type { BoardGroup } from '../../selectors/backtestBoard';
import { pct } from '../format';

/**
 * The ladder's buckets, in beta's order, with beta's own `${key} conf` label.
 *
 * ⚠ Keys must match `stackingLadder()`'s ('0' | '1' | '2' | '3+'). They are the
 * lookup, so a rename there silently empties this ladder rather than failing.
 */
const LADDER_BUCKETS = ['0', '1', '2', '3+'] as const;

/**
 * Beta's threshold for "this confluence is pulling its weight". A coin-flip is
 * not an edge, so 50% exactly reads as the good colour — reproduced as-is.
 */
const WIN_RATE_GOOD = 50;

/**
 * Bar width as a percentage of the track. Presentation only: the win rate
 * already arrives on a 0–100 scale, so this is the same number, floored.
 *
 * ⚠ The floor of 2 is beta's (`Math.max(2,g.wr||0)`) and is load-bearing — a 0%
 * confluence with no bar at all looks like a rendering failure rather than a
 * result, and the row's number is what carries the meaning anyway.
 */
function barWidth(winRate: number | null): number {
  return Math.max(2, winRate ?? 0);
}

/** Neutral for "no decided trades", never red. See the header. */
function toneClass(winRate: number | null): string {
  if (winRate === null) return 'bd-cf-muted';
  return winRate >= WIN_RATE_GOOD ? 'bd-cf-pos' : 'bd-cf-neg';
}

export interface ResultsConfluenceProps {
  /** One group per confluence, already sorted best win rate first. */
  byConfluence: readonly BoardGroup[];
  /** The 0 / 1 / 2 / 3+ stacking buckets. May be missing any of the four. */
  ladder: readonly BoardGroup[];
}

export function ResultsConfluence({ byConfluence, ladder }: ResultsConfluenceProps) {
  const byKey = new Map(ladder.map((g) => [g.key, g]));

  return (
    <div className="bd-cf-card" data-testid="results-confluence">
      <div className="bd-cf-head">
        <span className="bd-cf-eyebrow">Confluence</span>
        <h3 className="bd-cf-title">What confirms the setup</h3>
        <span className="bd-cf-note">win rate when present</span>
      </div>

      {byConfluence.length === 0 ? (
        // ⚠ Beta leaves this region blank, which reads as a broken card. One
        // sentence naming the reason costs nothing and cannot be misread.
        <div className="bd-cf-empty">No confluences recorded on these samples.</div>
      ) : (
        <>
          <div className="bd-cf-rows">
            {byConfluence.map((g) => (
              <div className="bd-cf-row" key={g.key} data-testid="confluence-row">
                <span className="bd-cf-name">{g.label}</span>
                <div className="bd-cf-track">
                  <span
                    className={
                      'bd-cf-fill'
                      + (g.stats.winRate !== null && g.stats.winRate >= WIN_RATE_GOOD
                        ? '' : ' bd-cf-fill-dim')
                    }
                    style={{ width: `${barWidth(g.stats.winRate)}%` }}
                  />
                </div>
                <span className={`bd-cf-val ${toneClass(g.stats.winRate)}`}>
                  {pct(g.stats.winRate, 0)}
                  {' '}
                  <small className="bd-cf-n">· n={g.stats.n}</small>
                </span>
              </div>
            ))}
          </div>

          {/*
            🚨 The line that stops a reader summing the bars above. It sits with
            the bars, not in a footnote, because the sum is attempted the moment
            the counts are visible.
          */}
          <p className="bd-cf-caveat">
            A sample counts once for every confluence it carried, so these rows
            overlap — their counts do not add up to the book.
          </p>
        </>
      )}

      <div className="bd-cf-ladder-head">
        <span className="bd-cf-eyebrow">Win rate by confluences stacked</span>
        {/* The other half of the same warning: here the counts DO add up. */}
        <span className="bd-cf-ladder-note">each sample counted once</span>
      </div>

      <div className="bd-cf-ladder">
        {LADDER_BUCKETS.map((key) => {
          const g = byKey.get(key);
          // ⚠ An absent bucket is drawn as n=0, not skipped. See the header.
          const winRate = g ? g.stats.winRate : null;
          const n = g ? g.stats.n : 0;
          return (
            <div
              className={
                'bd-cf-cell'
                + (key === '0' ? ' bd-cf-cell-baseline' : '')
              }
              key={key}
              data-testid="ladder-cell"
            >
              <div className="bd-cf-cell-k">{g ? g.label : `${key} conf`}</div>
              <div className={`bd-cf-cell-v ${toneClass(winRate)}`}>{pct(winRate, 0)}</div>
              <div className="bd-cf-cell-n">n={n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
